import React, { useState } from 'react';
import { View, TouchableOpacity, ActivityIndicator, Linking, ScrollView } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

// React Native Reusables Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Services & Components
import { billingAPI } from '../../services/api';
import AppHeaderBar from '../../components/layout/AppHeaderBar';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '@/context/AuthContext';
import { trackAuthEvent } from '../../services/analytics';
import { brand } from '../../design-system/theme/tokens';
import { checkboxA11y } from '@/utils/checkboxA11y';

// URLs
const landingBase = (process.env.EXPO_PUBLIC_LANDING_URL || 'https://mentorcoaching.ai').replace(/\/$/, '');
const TERMS_URL = process.env.EXPO_PUBLIC_TERMS_URL || `${landingBase}/terms`;
const PRIVACY_URL = process.env.EXPO_PUBLIC_PRIVACY_URL || `${landingBase}/privacy`;

// Professional Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#EF4444',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
  border: '#E5E7EB',
};

/**
 * Terms Acceptance Screen
 *
 * Displays terms of service and privacy policy acceptance before onboarding.
 * Required for legal compliance - users must explicitly agree before using the app.
 */
export default function TermsAcceptanceScreen() {
  const { t } = useTranslation();
  const { refreshAuth } = useAuth();
  const insets = useSafeAreaInsets();

  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!agreed) return;

    setLoading(true);
    setError(null);

    try {
      // Call API to mark terms as accepted
      await billingAPI.acceptTerms();
      trackAuthEvent('terms_accepted');
      // Refresh auth to update phase
      await refreshAuth();
    } catch (err: any) {
      setError(err?.message || t('terms.acceptError'));
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: COLORS.background }}>
      <AppHeaderBar />
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          padding: 24,
          paddingBottom: insets.bottom + 24
        }}
      >
        {/* Main Content */}
        <View className="flex-1 justify-center">
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="items-center pb-4">
              <View
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 32,
                  backgroundColor: `${COLORS.primary}15`,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 16,
                }}
              >
                <MaterialIcons name="gavel" size={32} color={COLORS.primary} />
              </View>
              <Text className="text-2xl font-bold text-foreground text-center">
                {t('terms.title')}
              </Text>
              <Text className="text-sm text-muted-foreground text-center mt-2">
                {t('terms.subtitle')}
              </Text>
            </CardHeader>

            <CardContent>
              {/* Terms Summary */}
              <View className="bg-muted/50 rounded-xl p-4 mb-6">
                <Text className="text-sm text-foreground leading-6">
                  {t('terms.summary')}
                </Text>
              </View>

              {/* Links to full documents */}
              <View className="mb-6">
                <TouchableOpacity
                  className="flex-row items-center py-3 border-b border-border"
                  onPress={() => Linking.openURL(TERMS_URL)}
                  accessibilityRole="link"
                >
                  <MaterialIcons name="description" size={20} color={COLORS.primary} />
                  <Text className="flex-1 ml-3 text-foreground font-medium">
                    {t('profile.termsOfService')}
                  </Text>
                  <MaterialIcons name="open-in-new" size={18} color={COLORS.text.muted} />
                </TouchableOpacity>

                <TouchableOpacity
                  className="flex-row items-center py-3"
                  onPress={() => Linking.openURL(PRIVACY_URL)}
                  accessibilityRole="link"
                >
                  <MaterialIcons name="security" size={20} color={COLORS.primary} />
                  <Text className="flex-1 ml-3 text-foreground font-medium">
                    {t('profile.privacyPolicy')}
                  </Text>
                  <MaterialIcons name="open-in-new" size={18} color={COLORS.text.muted} />
                </TouchableOpacity>
              </View>

              {/* Agreement Checkbox */}
              <TouchableOpacity
                className="flex-row items-start mb-6 p-4 rounded-xl border"
                style={{
                  borderColor: agreed ? COLORS.primary : COLORS.border,
                  backgroundColor: agreed ? `${COLORS.primary}05` : 'transparent',
                }}
                onPress={() => setAgreed(!agreed)}
                {...checkboxA11y(agreed)}
              >
                <MaterialIcons
                  name={agreed ? 'check-box' : 'check-box-outline-blank'}
                  size={24}
                  color={agreed ? COLORS.primary : COLORS.text.muted}
                  style={{ marginRight: 12, marginTop: 2 }}
                />
                <Text className="flex-1 text-sm text-foreground leading-5">
                  {t('terms.agreeLabel')}
                </Text>
              </TouchableOpacity>

              {/* Error Message */}
              {error && (
                <View className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4">
                  <Text className="text-red-700 text-sm text-center">{error}</Text>
                </View>
              )}

              {/* Continue Button */}
              <Button
                variant="default"
                size="lg"
                onPress={handleAccept}
                disabled={!agreed || loading}
                className="w-full"
              >
                {loading ? (
                  <ActivityIndicator color="white" />
                ) : (
                  <Text className="text-primary-foreground font-semibold">
                    {t('terms.continue')}
                  </Text>
                )}
              </Button>
            </CardContent>
          </Card>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
